import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from './useLanguage';

interface Translation {
  id: string;
  key: string;
  language: string;
  value: string;
  category: string;
}

interface TranslationCache {
  [key: string]: {
    [language: string]: string;
  };
}

const translationCache: TranslationCache = {};

export const useTranslations = () => {
  const { language } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [translations, setTranslations] = useState<Translation[]>([]);

  const loadTranslations = useCallback(async () => {
    if (Object.keys(translationCache).length === 0) {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('translations')
          .select('*')
          .order('key');

        if (error) throw error;

        if (data) {
          // Build cache
          data.forEach((translation) => {
            if (!translationCache[translation.key]) {
              translationCache[translation.key] = {};
            }
            translationCache[translation.key][translation.language] = translation.value;
          });
          setTranslations(data);
        }
      } catch (error) {
        console.error('Error loading translations:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadTranslations();
  }, [loadTranslations]);

  const t = useCallback((key: string, fallback?: string): string => {
    // Valeur dans la langue courante
    const valueLang = translationCache[key]?.[language];
    if (typeof valueLang === 'string' && valueLang.trim() !== '' && valueLang !== key) {
      return valueLang;
    }

    // Fallback sur FR si différent de la clé et non vide
    const valueFr = translationCache[key]?.['fr'];
    if (language !== 'fr' && typeof valueFr === 'string' && valueFr.trim() !== '' && valueFr !== key) {
      return valueFr;
    }

    // Sinon, utiliser le fallback fourni ou la clé
    return fallback ?? key;
  }, [language]);

  const addTranslation = async (key: string, language: string, value: string, category = 'general') => {
    try {
      const { data, error } = await supabase
        .from('translations')
        .upsert({
          key,
          language,
          value,
          category
        }, {
          onConflict: 'key,language'
        })
        .select()
        .single();

      if (error) throw error;

      // Update cache
      if (!translationCache[key]) {
        translationCache[key] = {};
      }
      translationCache[key][language] = value;

      return data;
    } catch (error) {
      console.error('Error adding translation:', error);
      throw error;
    }
  };

  const batchAddTranslations = async (translationsData: { key: string; language: string; value: string; category?: string }[]) => {
    try {
      const { data, error } = await supabase
        .from('translations')
        .upsert(translationsData, {
          onConflict: 'key,language'
        })
        .select();

      if (error) throw error;

      // Update cache
      if (data) {
        data.forEach((translation) => {
          if (!translationCache[translation.key]) {
            translationCache[translation.key] = {};
          }
          translationCache[translation.key][translation.language] = translation.value;
        });
      }

      return data;
    } catch (error) {
      console.error('Error batch adding translations:', error);
      throw error;
    }
  };

  return {
    t,
    translations,
    isLoading,
    addTranslation,
    batchAddTranslations,
    loadTranslations
  };
};